import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "@/lib/api";
import { getApiUrl } from "@/lib/api";
import { TutorialRenderer } from "@/components/Tutorials/TutorialBlocks";
import { ArrowLeft, Loader2, MessageCirclePlus, Send, Eye } from "lucide-react";
import { toast } from "sonner";

const API = `${getApiUrl()}/api`;

export default function TutorialDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [tut, setTut] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fbOpen, setFbOpen] = useState(false);
  const [fbMsg, setFbMsg] = useState("");
  const [fbBusy, setFbBusy] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/tutorials/${id}`);
        setTut(res.data);
      } catch (e) { toast.error("Tutorial not found"); navigate("/tutorials"); } finally { setLoading(false); }
    })();
  }, [id, navigate]);

  const submitFeedback = async () => {
    if (!fbMsg.trim()) return;
    setFbBusy(true);
    try {
      await axios.post(`${API}/tutorials/feedback`, { message: fbMsg.trim(), tutorialId: id, tutorialTitle: tut?.title });
      toast.success("Thanks! Your question was sent to the team.");
      setFbMsg(""); setFbOpen(false);
    } catch (e) { toast.error("Failed to send."); } finally { setFbBusy(false); }
  };

  if (loading) return <div className="flex justify-center py-24"><Loader2 className="w-6 h-6 animate-spin text-white/40" /></div>;
  if (!tut) return null;

  return (
    <div className="max-w-3xl mx-auto p-6 space-y-6" data-testid="tutorial-detail-page">
      <button onClick={() => navigate("/tutorials")} className="inline-flex items-center gap-1.5 text-sm text-white/60 hover:text-white" data-testid="tutorial-back">
        <ArrowLeft className="w-4 h-4" /> Back to Tutorials
      </button>

      <div className="space-y-2">
        <h1 className="text-3xl font-bold text-white">{tut.title}</h1>
        {tut.summary && <p className="text-white/55">{tut.summary}</p>}
        <div className="flex items-center gap-2 text-[11px] text-white/40">
          <span className="inline-flex items-center gap-1"><Eye className="w-3 h-3" /> {tut.views || 0} views</span>
        </div>
      </div>

      <TutorialRenderer blocks={tut.blocks || []} />

      <div className="pt-6 border-t border-white/10">
        <button onClick={() => setFbOpen(true)} className="inline-flex items-center gap-1.5 text-sm px-3 py-2 rounded-lg bg-cyan-500/20 border border-cyan-400/30 text-cyan-100 hover:bg-cyan-500/30" data-testid="detail-ask-btn">
          <MessageCirclePlus className="w-4 h-4" /> Still stuck? Ask a question
        </button>
      </div>

      {fbOpen && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => setFbOpen(false)}>
          <div className="bg-[#0A0A0A] border border-white/10 rounded-xl p-5 w-full max-w-md space-y-3" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-white">Ask about &ldquo;{tut.title}&rdquo;</h3>
            <textarea value={fbMsg} onChange={(e) => setFbMsg(e.target.value)} rows={4} placeholder="What would you like help with?" className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm text-white/90 focus:outline-none focus:border-cyan-400/50" data-testid="detail-feedback-message" />
            <div className="flex justify-end gap-2">
              <button onClick={() => setFbOpen(false)} className="text-sm px-3 py-1.5 rounded border border-white/10 text-white/70">Cancel</button>
              <button onClick={submitFeedback} disabled={fbBusy || !fbMsg.trim()} className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded bg-cyan-500 hover:bg-cyan-600 text-white disabled:opacity-50" data-testid="detail-feedback-submit">
                {fbBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
